import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  JSONAPISingleRelationship
} from "./Helpers";
import {TaskStatus} from "../modules/tasks/enums/TaskStatus";
import {Priority} from "../modules/tasks/enums/Priority";
import {TaskType} from "../modules/tasks/enums/TaskType";
import {deserializePatientProgram, PatientProgram} from "./patient/PatientProgram";
import {deserializeTaskRelation, TaskRelation} from './task/Relation';
import {Cron, parseCronList} from "helpers/forms/Cron";
import {parseISO} from "date-fns";
import {Component} from "./program/Component";
import {isNull} from "underscore";
import {deserializeVersion, Version, VersionDeserializer} from "./Version";

enum TaskAssignCondition {
  TmzIsPba = 'tmz_is_pba',
  TmzIsNotPba = 'tmz_is_not_pba'
}
type AssignedToClasses = "Patient" | "Patient::Program" | "Physician" | "Program::Component" | "Role" | "Survey::Question::Answer::Simple";
type AssignedToType = JSONAPIReference<"patient" | "physician" | "role"> | PatientProgram | Component;
type TaskAssignConditionType = `${TaskAssignCondition}`;
type TaskNotificationEventType = 'comment' | 'maturity' | 'assignment' | 'escalation';

const defaultCronTask: Readonly<Cron> = Object.freeze({
  period: 'day',
  time: parseISO("2020-01-01T08:15:00"),
  monthDay: '1',
  months: parseCronList("*", 12),
  weekdays: parseCronList("*", 7)
});

interface TaskSchedule extends DeserializedRecord {
  cron: string,
  startAt: Date,
  endAt: Date,
}

interface CronTaskSchedule extends DeserializedRecord {
  cron: Cron[],
  startAt?: Date,
  endAt?: Date,
}

interface TaskNotification extends DeserializedRecord {
  eventType: TaskNotificationEventType
}

interface BaseTask extends DeserializedRecord<"task"> {
  title: string,
  cronEnabled?: boolean;
  description: string,
  deletedAt?: Date;
  deleteDisabled: boolean;
  finalDeleteDisabled: boolean;
  status: TaskStatus,
  taskType: TaskType,
  assignedToType: AssignedToClasses,
  assignedToId: string,
  assignedTo?: AssignedToType,
  isDueAtWholeDay: boolean,
  taskRelations: TaskRelation[],
  taskComments: TaskComment[],
  isEscalationEnabled: boolean,
  escalateInHours: number,
  escalatedAt: Date,
  taskSchedule?: TaskSchedule,
  versions: Version<Task>[];
}

type GoalChildren = Omit<Goal, "children">;

interface Goal
  extends Omit<BaseTask, "dueInDays" | "taskComments" | "taskNotifications" | "taskRelations"> {
  taskType: TaskType.Goal;
  authorId: string;
  authorType: string;
  children: GoalChildren[];
  isMandatory?: boolean;
  taskAssignTemplate?: TaskAssignTemplate;
}

interface Task extends BaseTask {
  cronSchedule?: CronTaskSchedule;
  dueAt: Date;
  dueInDays?: number;
  durationMinutes?: number;
  priority: Priority;
  taskAssignTemplate?: TaskAssignTemplate;
  taskNotifications?: TaskNotification[];
}

const isGoal = (payload: Goal | Task): payload is Goal => payload.taskType === TaskType.Goal;

interface TaskData extends JSONAPIRecord {
  relationships: {
    assigned_to: JSONAPISingleRelationship;
    task_relations: JSONAPIManyRelationship;
    task_notifications: JSONAPIManyRelationship;
    task_comments: JSONAPIManyRelationship;
    children: JSONAPIManyRelationship;
    task_schedule: JSONAPISingleRelationship;
    task_assign_template: JSONAPISingleRelationship;
    versions: JSONAPIManyRelationship;
  }
}

interface TaskAssignTemplate extends DeserializedRecord {
  assignedToType: AssignedToClasses;
  assignedToId: string;
  assignedTo?: AssignedToType;
  preCreateInDays: number;
  scheduleDelayInDays: number;
  isPreviousIntervalIncluded?: boolean;
  assignCondition?: TaskAssignConditionType,
  isTemplate?: boolean
}

interface TaskAssignTemplateData extends JSONAPIRecord {
  relationships: {
    assigned_to: JSONAPISingleRelationship;
  }
}

interface TaskComment extends DeserializedRecord {
  comment: string,
  visibleInReport: boolean,
  physicianId: string,
  physician: JSONAPIReference<"physician">
}

interface TaskCommentData extends JSONAPIRecord {
  relationships: {
    physician: JSONAPISingleRelationship;
  }
}

const assignedToType = (type: string): AssignedToClasses => {
  switch (type) {
    case 'role':
      return "Role";
    case 'physician':
      return "Physician";
    case "patient_program":
      return "Patient::Program";
    case 'program_component':
      return "Program::Component";
    case 'patient':
      return 'Patient';
    case "simple":
      return "Survey::Question::Answer::Simple";
    default:
      return null;
  }
};

const deserializeTaskComments: Deserializer<TaskComment, TaskCommentData> = (data) => {
  const physician = deserializeSingleRelationship<JSONAPIReference<"physician">>(data.relationships.physician);

  return {
    ...deserializeItem(data),
    physician
  };
};

const deserializerByAssignedToType = (assignedToType: string) => {
  switch (assignedToType) {
    case "patient_program":
      return deserializePatientProgram;
    default:
      return deserializeItem;
  }
};

const deserializeTaskNotification: Deserializer<TaskNotification> = (data: JSONAPIRecord): TaskNotification => ({
  ...deserializeItem<TaskNotification>(data)
});

const deserializeTaskAssignTemplate: Deserializer<TaskAssignTemplate, TaskAssignTemplateData> = (data, included) => {
  if (isNull(data.relationships.assigned_to.data)) {
    return {
      ...deserializeItem<TaskAssignTemplate>(data)
    };
  }

  const assignedTo = deserializeSingleRelationship<AssignedToType>(
    data.relationships.assigned_to,
    included,
    deserializerByAssignedToType(data.relationships.assigned_to.data?.type)
  );

  const deserializedData = deserializeItem<TaskAssignTemplate>(data);

  return {
    ...deserializedData,
    assignedTo: assignedTo,
    assignedToId: assignedTo.id
  };
};

const deserializeTask: Deserializer<Task, TaskData> = (data, included) => {
  const assignedTo = deserializeSingleRelationship<AssignedToType>(
    data.relationships.assigned_to,
    included,
    deserializerByAssignedToType(data.relationships.assigned_to.data.type)
  );

  const taskRelations = deserializeManyRelationship<TaskRelation>(data.relationships.task_relations, included, deserializeTaskRelation);
  const taskComments = deserializeManyRelationship<TaskComment>(data.relationships.task_comments, included, deserializeTaskComments);
  // @ts-expect-error GoalChildren are only a subset of Goal which is a subset of Task
  const children = deserializeManyRelationship<GoalChildren>(data.relationships.children, included, deserializeTask);
  const deserializedData = deserializeItem<Task>(data);
  const taskSchedule = deserializeSingleRelationship<TaskSchedule>(data.relationships.task_schedule, included, deserializeItem);
  const versions = deserializeManyRelationship(data.relationships.versions, included, deserializeVersion as VersionDeserializer<Task>);

  const taskNotifications = deserializeManyRelationship<TaskNotification>(data.relationships.task_notifications,
    included, deserializeTaskNotification);

  const taskAssignTemplate = deserializeSingleRelationship<TaskAssignTemplate>(
    data.relationships.task_assign_template,
    included,
    deserializeTaskAssignTemplate);

  return {
    ...deserializedData,
    taskAssignTemplate,
    versions,
    //NOTE(Keune): Tasks with comments remain in the recycle bin
    deleteDisabled: taskComments.length > 0 && data.attributes.deleted_at !== null,
    assignedTo: assignedTo,
    assignedToId: assignedTo.id,
    assignedToType: assignedToType(assignedTo.type),
    taskComments: taskComments,
    taskRelations: taskRelations,
    taskNotifications,
    children: children,
    cronEnabled: taskSchedule?.id !== undefined,
    taskSchedule,
    cronSchedule: {
      id: taskSchedule?.id,
      startAt: taskSchedule?.startAt,
      endAt: taskSchedule?.endAt,
      cron: taskSchedule?.cron as unknown as Cron[] ?? [defaultCronTask]
    } as CronTaskSchedule,
    finalDeleteDisabled: taskComments.length > 0
  };
};

export {
  deserializeTask,
  deserializeTaskComments,
  Goal,
  GoalChildren,
  isGoal,
  Task,
  TaskComment,
  assignedToType as assignedToTypeMap,
  AssignedToType,
  defaultCronTask,
  CronTaskSchedule,
  TaskSchedule,
  TaskAssignTemplate,
  TaskAssignCondition,
  TaskNotificationEventType,
  TaskNotification
};
